import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, FileText, PenTool, Target, Copy } from 'lucide-react';

export default function CoverLetterPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.coverLetter} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Professional Writing</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Cover Letter Writing Service
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Complement your resume with a professional cover letter tailored to your target roles. Easy to customize for specific applications.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* What's Included */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            What's Included in Cover Letter Support
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: PenTool, title: 'Professional Writing', desc: 'A well-crafted cover letter that introduces you effectively and complements your resume.' },
              { icon: Target, title: 'Tailored to Target Role', desc: 'Written for your specific target role or industry, not a generic template.' },
              { icon: Copy, title: 'Easy to Customize', desc: 'Designed so you can easily adapt it for different job applications.' },
              { icon: FileText, title: 'Matching Format', desc: 'Formatted to visually complement your resume for a cohesive application.' },
              { icon: Check, title: 'Key Achievements', desc: 'Highlights your most relevant achievements and value proposition.' },
              { icon: Check, title: 'DOCX Delivery', desc: 'Editable format so you can customize for each application.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* When You Need One */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              When Do You Need a Cover Letter?
            </h2>
            
            <div className="space-y-4">
              {[
                'When the job posting specifically requests a cover letter',
                'When you\'re making a career change and need to explain the transition',
                'When you have gaps in employment you want to address proactively',
                'When you\'re applying to a company where you have a strong interest',
                'When you want to stand out from other applicants with similar resumes',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                  <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-600 mt-8 text-center">
              <strong>Note:</strong> Not every application needs a cover letter. If you're unsure, mention it in your intake and we'll advise based on your situation.
            </p>
          </div>
        </section>

        {/* Bundle with Resume */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <Card className="p-8 rounded-3xl bg-teal-50 border-2 border-teal-200">
            <div className="text-center">
              <Badge className="bg-teal-600 text-white mb-4">Common Add-On</Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                Add Cover Letter to Your Resume Project
              </h3>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
                Many clients add a cover letter to their resume rewrite for a complete application package. Just mention it in your intake form.
              </p>
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">
                Learn about Resume Rewrite →
              </Link>
            </div>
          </Card>
        </section>

        {/* Related Services */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/linkedin-optimization" className="text-teal-700 hover:text-teal-800 underline">LinkedIn Optimization</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View All Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Need a professional cover letter?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and check the cover letter option. We'll include it in your confirmed scope.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
